/**
 * PageLoadingSpinner — a full-area centred loading indicator.
 *
 * Replaces the inconsistent pattern of manually wrapping CircularProgress
 * in a centred Box across the codebase.
 *
 * @example
 * if (isLoading) return <PageLoadingSpinner />;
 * if (isLoading) return <PageLoadingSpinner label="Fetching cases…" />;
 * // Inline variant for partial-page areas:
 * <PageLoadingSpinner variant="inline" />
 */
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface PageLoadingSpinnerProps {
  /** Optional text below the spinner */
  label?: string;
  /** Size of the spinner (default: 36) */
  size?: number;
  /**
   * - "page": centres vertically in the full remaining viewport (default)
   * - "inline": centres within its parent container (200px min-height)
   */
  variant?: 'page' | 'inline';
}

export default function PageLoadingSpinner({
  label,
  size = 36,
  variant = 'page',
}: PageLoadingSpinnerProps) {
  const { t } = useTranslation();
  return (
    <Box
      role="status"
      aria-label={label ?? t('common.states.loading')}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        ...(variant === 'page'
          ? { minHeight: '60vh', width: '100%' }
          : { minHeight: 200, width: '100%' }),
      }}
    >
      <CircularProgress size={size} />
      <Typography variant="body2" color="text.secondary">{label ?? t('common.states.loading')}</Typography>
    </Box>
  );
}
